import type { TicketChannelV3Condition as TicketChannelV3ConditionType } from "@redotech/redo-flows-common/rules-v3/schemas";
import type { MessageChannelV3 } from "@redotech/redo-flows-common/rules-v3/types";
import { useTranslation } from "@redotech/redo-i18n";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { channelDisplayNames, channelItems } from "../channel-v3-items";

export const TicketChannelV3Condition = memo(function TicketChannelV3Condition({
  condition,
  onChange,
  editableFields,
}: {
  condition: TicketChannelV3ConditionType;
  onChange: (updated: TicketChannelV3ConditionType) => void;
  /**
   * When omitted, all fields are editable. When provided, only fields
   * in the set are editable; others render read-only. Used by the
   * default-rule edit page.
   */
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const channelsEditable =
    editableFields === undefined || editableFields.has("channels");

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems: RedoListItem<MessageChannelV3>[] = useMemo(() => {
    return (condition.channels || []).map((channel) => ({
      id: channel,
      type: "text" as const,
      text: channelDisplayNames()[channel],
      value: channel,
    }));
  }, [condition.channels]);

  const displayItem: RedoListItem<MessageChannelV3> | undefined =
    useMemo(() => {
      if (selectedItems.length === 0) {
        return undefined;
      }
      if (selectedItems.length === 1) {
        return selectedItems[0];
      }
      return {
        id: "display",
        type: "text" as const,
        text: selectedItems
          .flatMap((item) => ("text" in item ? [item.text] : []))
          .join(", "),
        value: "gmail" as MessageChannelV3,
      };
    }, [selectedItems]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="secondary">
        {t("Channel", { context: "support conversation channel" })}
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={!channelsEditable}
        isDropdownOpen={dropdownOpen}
        onClick={() => channelsEditable && setDropdownOpen(!dropdownOpen)}
        placeholder={t("Select channels")}
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={channelItems()}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!channelsEditable) {
            return;
          }
          onChange({ ...condition, channels: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
